import type { FigureListKind } from '@alpod-ai-writer/shared';
import clsx from 'clsx';

import { useDocumentEditor } from '../hooks/useDocumentEditor.hook';
import { type FigureItem,useFigureList } from '../hooks/useFigureList';
import { getSectionLink, useSectionNav } from '../hooks/useSectionNav.hook';
import { limitString } from '../utils/string.utils';

const KIND_FILTER: Record<FigureListKind, string[]> = {
    all: ['image', 'table', 'chart', 'formula', 'map', 'other'],
    images: ['image'],
    tables: ['table'],
    charts: ['chart'],
    maps: ['map'],
    formulas: ['formula'],
    other: ['other'],
};

export interface FigureListEditorProps {
    figureListKind?: FigureListKind;
}

export const FigureListEditor: React.FC<FigureListEditorProps> = ({ figureListKind = 'all' }) => {
    const { figures: allFigures } = useFigureList();
    const figures = allFigures.filter((f) => KIND_FILTER[figureListKind].includes(f.kind));

    return (
        <div className='flex flex-col gap-6 px-1 md:px-0'>
            <div className='flex flex-col gap-2'>
                {figures.map((item) => (
                    <FigureListEntry
                        key={item.id}
                        entryKey={item.id}
                        item={item}
                    />
                ))}
                {!figures.length && (
                    <div className='text-gray-500 text-left border border-gray-300 text-sm p-4 rounded-lg'>V dokumentu zatím nebyly nalezeny žádné položky. Např. obrázek se zde objeví po jeho vložení do dokumentu a nastavení popisku.</div>
                )}
            </div>
        </div>
    );
};

const FigureListEntry: React.FC<{
    entryKey: string;
    item: FigureItem;
}> = ({
    entryKey,
    item
}) => {
    const editor = useDocumentEditor();
    const { currentSection } = useSectionNav(editor.template.config.sections);

    return (
        <div className='relative'>
            <div
                className={clsx(
                    'w-full flex flex-col rounded-xl overflow-hidden shadow-[0px_0px_10px_transparent] transition-all duration-200 border border-gray-300',
                )}
            >
                <div
                    className={clsx(
                        'flex flex-row items-start justify-start pl-5 pr-4 py-4 gap-1 transition-colors duration-200',
                    )}
                >
                    <a id={`${currentSection?.sectionId}/${entryKey}`} />
                    <div
                        className='flex flex-col grow items-start justify-start text-left gap-1'
                    >
                        <span className='block text-sm'>
                            <span className='font-semibold'>{{
                                'image': 'Obrázek',
                                'table': 'Tabulka',
                                'chart': 'Graf',
                                'formula': 'Vzorec',
                                'map': 'Mapa',
                                'other': ''
                            }[item.kind]} </span>
                            <span>{item.label || '(prázdná položka)'}</span>
                        </span>
                        <span className='flex flex-row flex-wrap gap-2 pt-1'>
                            <a
                                href={getSectionLink(item.ref.sectionId, item.ref.headingId)}
                                className='text-xs text-primary-500 underline hover:no-underline'
                                onClick={(ev) => ev.stopPropagation()}
                            >{item.ref.sectionTitle}{item.ref.headingTitle ? ' - ' + limitString(item.ref.headingTitle, 20) : ''}</a>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};
